import React, { useRef, useEffect, useCallback } from 'react'

const PILL_STYLE = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 8px',
    margin: '0 2px',
    backgroundColor: '#ccfbf1',
    color: '#115E59',
    border: '1px solid #5eead4',
    borderRadius: '9999px',
    fontSize: '12px',
    fontFamily: 'ui-monospace, Consolas, monospace',
    lineHeight: '1.6',
    whiteSpace: 'nowrap',
    cursor: 'default',
    userSelect: 'all',
    verticalAlign: 'middle',
}

function textToHtml(text, mergeTags) {
    if (!text) return ''
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    return escaped.replace(/\{\{(\w+)\}\}/g, (match, tagName) => {
        const def = mergeTags.find((t) => t.tag === `{{${tagName}}}`)
        const label = def?.label ?? tagName
        const style = Object.entries(PILL_STYLE)
            .map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`)
            .join(';')
        return `<span data-merge-tag="${tagName}" contenteditable="false" style="${style}" title="{{${tagName}}}">${label}</span>`
    })
}

function htmlToText(el) {
    let result = ''
    el.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent
        } else if (node.nodeType === Node.ELEMENT_NODE && node.dataset.mergeTag) {
            result += `{{${node.dataset.mergeTag}}}`
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            result += htmlToText(node)
        }
    })
    return result
}

function saveCursorPosition(el) {
    const sel = window.getSelection()
    if (!sel.rangeCount || !el.contains(sel.anchorNode)) return null
    const range = sel.getRangeAt(0)
    const preRange = document.createRange()
    preRange.selectNodeContents(el)
    preRange.setEnd(range.startContainer, range.startOffset)
    return preRange.toString().length
}

function restoreCursorPosition(el, offset) {
    if (offset == null) return
    const sel = window.getSelection()
    const range = document.createRange()
    let current = 0
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
    let node = walker.nextNode()
    while (node) {
        const len = node.textContent.length
        if (current + len >= offset) {
            range.setStart(node, offset - current)
            range.collapse(true)
            sel.removeAllRanges()
            sel.addRange(range)
            return
        }
        current += len
        node = walker.nextNode()
    }
    // Fallback: place at end
    range.selectNodeContents(el)
    range.collapse(false)
    sel.removeAllRanges()
    sel.addRange(range)
}

export default function SubjectInput({ value, onChange, mergeTags = [], placeholder, onFocus, insertTagRef }) {
    const ref = useRef(null)
    const isComposing = useRef(false)

    // Expose insert method so parent can insert merge tags into subject at cursor position
    useEffect(() => {
        if (insertTagRef) {
            insertTagRef.current = (tagName) => {
                const el = ref.current
                if (!el) return
                const tag = `{{${tagName}}}`
                // Get cursor position in the plain text
                const pos = saveCursorPosition(el)
                const text = htmlToText(el)
                if (pos != null && pos <= text.length) {
                    const newValue = text.slice(0, pos) + tag + text.slice(pos)
                    onChange(newValue)
                } else {
                    // Fallback: append at end
                    onChange(text + tag)
                }
            }
        }
    }, [insertTagRef, onChange])

    // Sync HTML when value changes externally (not from user typing)
    const lastValue = useRef(value)
    useEffect(() => {
        if (!ref.current) return
        const currentText = htmlToText(ref.current)
        if (currentText !== value) {
            const pos = saveCursorPosition(ref.current)
            ref.current.innerHTML = textToHtml(value, mergeTags)
            restoreCursorPosition(ref.current, pos)
        }
        lastValue.current = value
    }, [value, mergeTags])

    const handleInput = useCallback(() => {
        if (isComposing.current) return
        const el = ref.current
        if (!el) return
        const text = htmlToText(el)
        if (text !== lastValue.current) {
            lastValue.current = text
            onChange(text)
            // Don't re-render pills during typing — cursor save/restore
            // breaks around contenteditable=false pill nodes. Pills are
            // re-rendered on blur and when value/mergeTags change via useEffect.
        }
    }, [onChange])

    const handlePaste = useCallback((e) => {
        e.preventDefault()
        const text = e.clipboardData.getData('text/plain')
        const sel = window.getSelection()
        if (sel && sel.rangeCount) {
            const range = sel.getRangeAt(0)
            range.deleteContents()
            range.insertNode(document.createTextNode(text))
            range.collapse(false)
            sel.removeAllRanges()
            sel.addRange(range)
        }
        handleInput()
        // Force immediate pill render after paste so {{tags}} don't flash as raw text
        const el = ref.current
        if (el) {
            const pastedText = htmlToText(el)
            el.innerHTML = textToHtml(pastedText, mergeTags)
        }
    }, [handleInput, mergeTags])

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') e.preventDefault()
    }, [])

    const handleBlur = useCallback(() => {
        const el = ref.current
        if (!el) return
        const text = htmlToText(el)
        el.innerHTML = textToHtml(text, mergeTags)
    }, [mergeTags])

    const isEmpty = !value

    return (
        <div className="relative flex-1">
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={handleBlur}
                onCompositionStart={() => { isComposing.current = true }}
                onCompositionEnd={() => { isComposing.current = false; handleInput() }}
                className="text-sm text-gray-900 border border-gray-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:border-transparent min-h-[34px] whitespace-nowrap overflow-x-auto"
                style={{ '--tw-ring-color': '#5eead4' }}
            />
            {isEmpty && (
                <div className="absolute left-3 top-1.5 text-sm text-gray-400 pointer-events-none">
                    {placeholder}
                </div>
            )}
        </div>
    )
}
